/* eslint-disable react/prop-types */
import { Box, Typography, Chip, Button } from "@mui/material";
import { Add } from "@mui/icons-material";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import StepInputCard from "./StepInputCard";

const StepInputsSection = ({
  nodeData,
  inputTypeList,
  handleInputTypeChange,
  addNewStepInput,
  handleDeleteStepInput,
  moveStepInputUp,
  moveStepInputDown,
  optionInputs,
  handleOptionInputChange,
  handleAddOption,
  removeOption,
  translationPrefix = "newWorkFlow",
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Box
      sx={{
        p: 3,
        mb: 3,
        bgcolor: theme.palette.mode === "dark" ? "grey.900" : "grey.50",
        borderRadius: 2,
        border: 1,
        borderColor: "divider",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            color="primary"
            gutterBottom
            sx={{
              fontWeight: "bold",
              mb: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {t(`${translationPrefix}.stepChecksTitle`)}
            {nodeData?.step_inputs?.length > 0 && (
              <Chip
                label={nodeData.step_inputs.length}
                size="small"
                color="primary"
                sx={{ ml: 1 }}
              />
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(`${translationPrefix}.stepInputsDescription`)}
          </Typography>
        </Box>
      </Box>

      {nodeData?.step_inputs?.length === 0 && (
        <Box
          sx={{
            p: 4,
            textAlign: "center",
            border: 2,
            borderStyle: "dashed",
            borderColor: "divider",
            borderRadius: 2,
            bgcolor: "background.paper",
            mb: 3,
          }}
        >
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {t(`${translationPrefix}.noStepInputsMessage`)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(`${translationPrefix}.noStepInputsInfo`)}
          </Typography>
        </Box>
      )}

      {nodeData?.step_inputs?.map((step_input, index) => {
        const inputType = inputTypeList.find(
          (option) => option.value === step_input.input_type_id
        );
        const showOptionsField =
          inputType &&
          ["selection", "multi_selection"].includes(inputType.input_key);

        return (
          <StepInputCard
            key={index}
            index={index}
            step_input={step_input}
            showOptionsField={showOptionsField}
            handleInputTypeChange={handleInputTypeChange}
            moveStepInputUp={moveStepInputUp}
            moveStepInputDown={moveStepInputDown}
            handleDeleteStepInput={handleDeleteStepInput}
            optionInputs={optionInputs}
            handleOptionInputChange={handleOptionInputChange}
            handleAddOption={handleAddOption}
            removeOption={removeOption}
            inputTypeList={inputTypeList}
            totalInputs={nodeData.step_inputs.length}
            translationPrefix={translationPrefix}
          />
        );
      })}

      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Button
          variant="outlined"
          startIcon={<Add />}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: "medium",
            px: 4,
            py: 1.5,
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            "&:hover": {
              background: alpha(theme.palette.primary.main, 0.04),
              borderColor: theme.palette.primary.main,
              transform: "translateY(-1px)",
              boxShadow: 2,
              color: theme.palette.primary.main,
            },
          }}
          onClick={addNewStepInput}
          aria-label={t(`${translationPrefix}.addStepInputButtonAria`)}
        >
          {t(`${translationPrefix}.addStepInputButton`)}
        </Button>
      </Box>
    </Box>
  );
};

export default StepInputsSection;
